import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ToastContainerProps, ToastItem } from './types'
import useStyles from './styles'
import { toast } from '@/controllers'
import { Container } from './Container'
import { ToastConfig } from '@/controllers/ToastController/types'
import { ANIMATION_DURATION, DEFAULT_CONFIGS } from './constants'
import { isBool } from '@/utils/isBool'

// TODO: abstrair parte da lógica para hooks/context
export const ToastContainer: FunctionComponent<ToastContainerProps> = props => {
  const styles = useStyles({ zIndex: props.zIndex })
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const [toHide, setToHide] = useState<string[]>([])

  const configsRef = useRef<ToastConfig>({
    autoClose: isBool(props.autoClose)
      ? props.autoClose
      : props.autoClose || DEFAULT_CONFIGS.autoClose,
    theme: props.theme || DEFAULT_CONFIGS.theme,
    position: props.position || DEFAULT_CONFIGS.position,
    variant: props.variant || DEFAULT_CONFIGS.variant,
  })
  useEffect(() => {
    configsRef.current = {
      autoClose: isBool(props.autoClose)
        ? props.autoClose
        : props.autoClose || DEFAULT_CONFIGS.autoClose,
      theme: props.theme || DEFAULT_CONFIGS.theme,
      position: props.position || DEFAULT_CONFIGS.position,
      variant: props.variant || DEFAULT_CONFIGS.variant,
    }
  }, [props])

  const autoCloseTimers = useRef<NodeJS.Timeout[]>([])
  useEffect(() => {
    const timers = autoCloseTimers.current
    return () => timers.forEach(timer => clearTimeout(timer))
  }, [])

  const removeTimer = useCallback((timer: NodeJS.Timeout) => {
    autoCloseTimers.current = autoCloseTimers.current.filter(t => t !== timer)
    clearTimeout(timer)
  }, [])

  const toastsRef = useRef<ToastItem[]>(toasts)
  useEffect(() => {
    toastsRef.current = toasts
  }, [toasts])

  const removeToastByMessageId = useCallback((messageId: string) => {
    const toRemove = toastsRef.current.find(
      item => item.payload.messageId === messageId,
    )
    toRemove?.payload.onClose && toRemove.payload.onClose()
    setToasts(prev => {
      return prev.filter(toastItem => toastItem.payload.messageId !== messageId)
    })
  }, [])

  const animatedRemoveToastByMessageId = useCallback(
    (messageId: string) => {
      setToHide(prev => [...prev, messageId])
      setTimeout(() => {
        removeToastByMessageId(messageId)
      }, ANIMATION_DURATION)
    },
    [removeToastByMessageId],
  )

  const removeToast = useCallback(
    (toastToRemove: ToastItem) => {
      animatedRemoveToastByMessageId(toastToRemove.payload.messageId)
    },
    [animatedRemoveToastByMessageId],
  )

  useEffect(
    () =>
      toast.subscribe('message', (message, payload) => {
        const toastItem = {
          message,
          payload: { ...configsRef.current, ...payload },
        }
        if (toastItem.payload.autoClose) {
          const timer = setTimeout(() => {
            animatedRemoveToastByMessageId(toastItem.payload.messageId)
            removeTimer(timer)
          }, toastItem.payload.autoClose)
          autoCloseTimers.current.push(timer)
        }

        setToasts(prevMessages => [...prevMessages, toastItem])
      }),
    [animatedRemoveToastByMessageId, removeTimer],
  )

  useEffect(
    () =>
      toast.subscribe('dismiss', messageId => {
        animatedRemoveToastByMessageId(messageId)
      }),
    [animatedRemoveToastByMessageId],
  )

  useEffect(
    () =>
      toast.subscribe('dismissAll', () => {
        toastsRef.current.forEach(({ payload }) => {
          animatedRemoveToastByMessageId(payload.messageId)
        })
      }),
    [animatedRemoveToastByMessageId],
  )

  const topLeftToasts = useMemo(
    () =>
      toasts.filter(toastItem => toastItem.payload?.position === 'top-left'),
    [toasts],
  )

  const topRightToasts = useMemo(
    () =>
      toasts.filter(
        toastItem =>
          toastItem.payload?.position === 'top-right' ||
          !toastItem.payload?.position,
      ),
    [toasts],
  )

  const bottomLeftToasts = useMemo(
    () =>
      toasts.filter(toastItem => toastItem.payload?.position === 'bottom-left'),
    [toasts],
  )

  const bottomRightToasts = useMemo(
    () =>
      toasts.filter(
        toastItem => toastItem.payload?.position === 'bottom-right',
      ),
    [toasts],
  )

  return (
    <div css={styles.root}>
      <Container
        toastItems={topLeftToasts}
        position="top-left"
        onCloseToast={removeToast}
        toHide={toHide}
      />
      <Container
        toastItems={topRightToasts}
        position="top-right"
        onCloseToast={removeToast}
        toHide={toHide}
      />
      <Container
        toastItems={bottomLeftToasts}
        position="bottom-left"
        onCloseToast={removeToast}
        toHide={toHide}
      />
      <Container
        toastItems={bottomRightToasts}
        position="bottom-right"
        onCloseToast={removeToast}
        toHide={toHide}
      />
    </div>
  )
}
